import { css } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import dayjs from 'dayjs';
import { startCase } from 'lodash-es';
import { OfficeUserName, OfficeUserNameWithAi, generateTrackingId } from '@frontend/call-intel-core';
import { Divider } from '@frontend/divider';
import { useTranslation, i18next } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { getAiOutcomeIcon as getIcon, OUTCOME_MAPPING } from '@frontend/phone-ui';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Accordion, Button, Chip, NakedUl, Text } from '@frontend/design-system';
import { getFullName, getOfficeUser, getStatusChipColor } from '../../utils/call-insight-panel-util';
import { PhoneTreePreview } from '../phone-tree-preview';

type CallDetailsSectionProps = {
  call: HydratedCallRecord;
  onClick: () => void;
};

export const CallDetailsSection = ({ call, onClick }: CallDetailsSectionProps) => {
  const { t } = useTranslation('call-insight-panel');

  const { getScopeName, isSingleTypeScope } = useAppScopeStore();

  const { startedAt, direction, status = '', locationId, callerNumber, dialedNumber, user, phoneTrees } = call;

  const statusChipColor = getStatusChipColor(status);

  const isMulti = !isSingleTypeScope;

  const isOutboundCall = direction === 'outbound';

  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const outcome = call.aiMetadata?.aiOutcome;
  const reason = call.aiMetadata?.aiCallReason;

  const hasVoiceAi = getCustomizationFlagValue('truelarkaivoice');

  return (
    <>
      <Accordion.Header title={t('About This Call')} />
      <Accordion.Body>
        <NakedUl css={styles.list}>
          <li>
            <Text weight='bold'>{t('Call Details')}</Text>
          </li>

          {isMulti && locationId && (
            <li>
              <Text size='medium' color='subdued'>
                {t('Location')}
              </Text>
              <Chip.Location maxWidth='300'>{getScopeName(locationId)}</Chip.Location>
            </li>
          )}

          <li>
            <Text size='medium' color='subdued'>
              {t('Time')}
            </Text>
            <Text size='medium'>{dayjs(startedAt).format('MMM D, YYYY, h:mm A')}</Text>
          </li>

          {hasVoiceAi && outcome && outcome !== AIOutcome.AI_OUTCOME_UNKNOWN && (
            <li>
              <Text size='medium' color='subdued' css={{ alignSelf: 'start' }}>
                {t('AI Call Outcome')}
              </Text>
              <AiOutcomeComponent outcome={outcome} info={reason} />
            </li>
          )}

          <li>
            <Text size='medium' color='subdued'>
              {t('Result')}
            </Text>
            {direction === 'outbound' && <span>-</span>}
            {direction === 'inbound' && (
              <Chip isResponsive variant={statusChipColor}>
                {startCase(status)}
              </Chip>
            )}
            {!direction && <span>-</span>}
          </li>

          {!isOutboundCall && <Divider />}

          {!isOutboundCall && (
            <li>
              <Text weight='bold'>{t('Call Path Overview')}</Text>
            </li>
          )}

          <li>
            <Text size='medium' color='subdued'>
              {t('Office Number')}
            </Text>
            <Text size='medium'>
              {direction
                ? formatPhoneNumber(direction === 'outbound' ? callerNumber : dialedNumber)
                : formatPhoneNumber(call.callIntelInsights?.officeNumber?.nationalNumber?.toString())}
            </Text>
          </li>

          <li>
            <Text size='medium' color='subdued'>
              {t('Office User')}
            </Text>
            {hasVoiceAi ? (
              <OfficeUserNameWithAi
                displayName={getOfficeUser(call, '--')}
                profileName={getFullName(user?.firstName, user?.lastName, '--')}
                userId={user?.userId || ''}
                outcome={outcome}
              />
            ) : (
              <OfficeUserName
                displayName={getOfficeUser(call, '--')}
                profileName={getFullName(user?.firstName, user?.lastName, '--')}
                userId={user?.userId || ''}
              />
            )}
          </li>

          {!isOutboundCall && !!phoneTrees && phoneTrees.length > 0 && (
            <li>
              <Text size='medium' color='subdued'>
                {t('Phone Tree')}
              </Text>
              <PhoneTreePreview ivrEvents={phoneTrees} />
            </li>
          )}

          {!isOutboundCall && (
            <>
              <li>
                <Button
                  onClick={onClick}
                  disabled={isOutboundCall}
                  variant='tertiary'
                  size='large'
                  label={t('View Call Path')}
                  showLabelWhenDisabled
                  hoverLabel={isOutboundCall && t('Not available for outbound calls.')}
                  trackingId={generateTrackingId({
                    prefix: 'CallInsights',
                    component: 'AboutThisCall',
                    context: 'ViewCallPath',
                  })}
                >
                  {t('View Call Path')}
                </Button>
              </li>
            </>
          )}
        </NakedUl>
      </Accordion.Body>
    </>
  );
};

const styles = {
  list: css`
    li {
      align-items: center;
      display: flex;
      justify-content: space-between;
      margin-bottom: ${theme.spacing(2)};

      .data-label {
        color: ${theme.colors.neutral50};
        text-wrap: nowrap;
      }
    }

    li:last-child {
      margin-bottom: ${theme.spacing(0)};
    }
  `,
};

const AiOutcomeComponent = ({ outcome, info }: { outcome: AIOutcome; info?: string }) => {
  const config = AI_OUTCOME_CONFIG[outcome];

  if (!config) return null;

  return (
    <div style={{ textAlign: 'end', display: 'grid', gap: theme.spacing(0.5) }}>
      <OutcomeDisplay config={config} />
      {info && (
        <Text size='small' color='subdued'>
          {info}
        </Text>
      )}
    </div>
  );
};

type OutcomeConfig = {
  outcome: (typeof OUTCOME_MAPPING)[keyof typeof OUTCOME_MAPPING];
  text: string;
  isBold?: boolean;
  secondaryText?: string;
};

const AI_OUTCOME_CONFIG: Record<string, OutcomeConfig> = {
  [AIOutcome.AI_OUTCOME_END_CALL]: {
    outcome: OUTCOME_MAPPING.SUCCESS,
    text: i18next.t('Successfully assisted the patient', { ns: 'calls' }),
  },
  [AIOutcome.AI_OUTCOME_FATAL]: {
    outcome: OUTCOME_MAPPING.FATAL,
    text: i18next.t('Error', { ns: 'calls' }),
    isBold: true,
  },
  [AIOutcome.AI_OUTCOME_ROUTE_MISSED]: {
    outcome: OUTCOME_MAPPING.ROUTE_MISSED,
    text: i18next.t('Needs attention: ', { ns: 'calls' }),
    secondaryText: i18next.t('Transfer missed', { ns: 'calls' }),
    isBold: true,
  },
  [AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED]: {
    outcome: OUTCOME_MAPPING.ROUTE_ANSWERED,
    text: i18next.t('Call transferred: ', { ns: 'calls' }),
    secondaryText: i18next.t('Office assisted', { ns: 'calls' }),
    isBold: true,
  },
};

const OutcomeDisplay = ({ config }: { config: OutcomeConfig }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    }}
  >
    <div style={{ justifySelf: 'end', display: 'grid' }}>{getIcon({ outcome: config.outcome })}</div>
    <div>
      <Text as='span' size='medium' weight={config.isBold ? 'bold' : undefined}>
        {config.text}
      </Text>
      {config.secondaryText && (
        <Text as='span' size='medium'>
          {config.secondaryText}
        </Text>
      )}
    </div>
  </div>
);
